/**
 * Student Portal — Component Library.
 *
 * Real inventory from GET /api/components (already includes brand_name).
 * Search + Brand + Size + Availability filters.
 * Card → detail drawer → request-with-quantity path (fastest possible flow).
 *
 * No mock data, no schema changes.  Only these fields exist on `components`:
 *   name, brand_id, brand_name, description, size, price, is_returnable,
 *   total_stock, issued_qty, returned_old_stock, reissuable_stock,
 *   stock_quantity (= total_stock − issued_qty, computed by backend),
 *   low_stock_threshold.
 * Component Type / SKU / image_url deliberately NOT rendered because they
 * do not exist on the model.
 */
import React, { useMemo, useState } from 'react';
import { Layers, Filter, Package, RotateCcw, Minus, Plus, X, SlidersHorizontal } from 'lucide-react';
import {
  SearchInput, EmptyState, StatusPill,
  ComponentSilhouette, ComponentMonogram,
} from '../ui';

const availabilityBucket = (c) => {
  const stock = Number(c.stock_quantity || 0);
  const thr = Number(c.low_stock_threshold || 0);
  if (stock <= 0) return 'out';
  if (stock <= thr) return 'low';
  return 'in';
};

const availabilityPill = (bucket) => {
  if (bucket === 'out') return { variant: 'bad',  label: 'Out of stock' };
  if (bucket === 'low') return { variant: 'warn', label: 'Low stock' };
  return                       { variant: 'ok',   label: 'In stock' };
};

export default function LibraryPage({ ctx }) {
  const { components, brands, openNewRequest } = ctx;

  const [q, setQ] = useState('');
  const [brandFilter, setBrandFilter] = useState('all');
  const [sizeFilter, setSizeFilter] = useState('all');
  const [availabilityFilter, setAvailabilityFilter] = useState('all');
  const [detail, setDetail] = useState(null); // component object
  const [sheetOpen, setSheetOpen] = useState(false); // mobile filter sheet

  // Derived list of distinct sizes present in the current catalogue.
  const sizes = useMemo(() => {
    const set = new Set();
    components.forEach(c => { if (c.size) set.add(c.size); });
    return Array.from(set).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
  }, [components]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return components.filter(c => {
      if (brandFilter !== 'all' && c.brand_id !== brandFilter) return false;
      if (sizeFilter !== 'all' && c.size !== sizeFilter) return false;
      const bucket = availabilityBucket(c);
      if (availabilityFilter === 'in-stock' && bucket === 'out') return false;
      if (availabilityFilter === 'low' && bucket !== 'low') return false;
      if (availabilityFilter === 'out' && bucket !== 'out') return false;
      if (!needle) return true;
      const hay = `${c.name || ''} ${c.brand_name || ''} ${c.size || ''} ${c.description || ''}`.toLowerCase();
      return hay.includes(needle);
    });
  }, [components, q, brandFilter, sizeFilter, availabilityFilter]);

  const hasActiveFilters = q || brandFilter !== 'all' || sizeFilter !== 'all' || availabilityFilter !== 'all';
  const activeFilterCount =
    (brandFilter !== 'all' ? 1 : 0) +
    (sizeFilter !== 'all' ? 1 : 0) +
    (availabilityFilter !== 'all' ? 1 : 0);
  const clearFilters = () => {
    setQ(''); setBrandFilter('all'); setSizeFilter('all'); setAvailabilityFilter('all');
  };

  return (
    <>
      <div>
        <span className="sp-eyebrow">Portal</span>
        <h1 className="sp-page-title" data-testid="portal-library-title">Component Library</h1>
        <p className="sp-page-lede">
          Find the implant component you need for your clinical session. Live availability from the Department of Implantology inventory.
        </p>
      </div>

      {/* Search + filters */}
      <div className="sp-card sp-mt-5" style={{ padding: 20 }} data-testid="library-filters">
        <SearchInput
          value={q}
          onChange={setQ}
          placeholder="Search component, brand or size…"
          testId="library-search"
          autoFocus
        />

        {/* Desktop inline filter grid — hidden on mobile via CSS */}
        <div
          className="sp-filter-grid-desktop sp-mt-3"
          style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 12 }}
        >
          <div>
            <label className="sp-field-label">Brand</label>
            <select
              className="sp-select"
              value={brandFilter}
              onChange={(e) => setBrandFilter(e.target.value)}
              data-testid="library-brand-filter"
            >
              <option value="all">All brands</option>
              {brands.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </div>
          <div>
            <label className="sp-field-label">Size</label>
            <select
              className="sp-select"
              value={sizeFilter}
              onChange={(e) => setSizeFilter(e.target.value)}
              disabled={sizes.length === 0}
              data-testid="library-size-filter"
            >
              <option value="all">All sizes</option>
              {sizes.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="sp-field-label">Availability</label>
            <select
              className="sp-select"
              value={availabilityFilter}
              onChange={(e) => setAvailabilityFilter(e.target.value)}
              data-testid="library-availability-filter"
            >
              <option value="all">All items</option>
              <option value="in-stock">In stock</option>
              <option value="low">Low stock</option>
              <option value="out">Out of stock</option>
            </select>
          </div>
        </div>

        {/* Mobile filter pill — visible only under the 780px breakpoint */}
        <div className="sp-filter-bar sp-mt-3">
          <button
            type="button"
            className="sp-filter-pill"
            onClick={() => setSheetOpen(true)}
            data-testid="library-mobile-filter-pill"
          >
            <SlidersHorizontal size={15} strokeWidth={1.8} />
            <span>Filter</span>
            {activeFilterCount > 0 && (
              <span className="sp-filter-count" data-testid="library-mobile-filter-count">{activeFilterCount}</span>
            )}
          </button>
          <span className="sp-subtle sp-mono-num">{filtered.length} / {components.length}</span>
        </div>

        <div className="sp-mt-3 sp-flex-between">
          <span className="sp-subtle">
            <Filter size={11} strokeWidth={1.7} style={{ verticalAlign: -1, marginRight: 4 }} />
            Showing <strong className="sp-mono-num" style={{ color: 'var(--diis-ink)' }}>{filtered.length}</strong> of {components.length}
          </span>
          {hasActiveFilters && (
            <button
              type="button"
              className="sp-btn sp-btn-ghost sp-btn-sm"
              onClick={clearFilters}
              data-testid="library-clear-filters"
            >Clear filters</button>
          )}
        </div>
      </div>

      {/* Results */}
      <div className="sp-mt-5">
        {components.length === 0 ? (
          <EmptyState
            icon={Layers}
            title="NO COMPONENTS AVAILABLE YET"
            message="Once inventory is added by Super Admin / Admin, components will appear here automatically. This page reads directly from the live database."
            testId="library-empty-catalog"
          />
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={Layers}
            title="No components match those filters"
            message="Try widening the search, changing the brand, or clearing the availability filter."
            action={<button type="button" className="sp-btn sp-btn-ghost sp-btn-sm" onClick={clearFilters}>Clear filters</button>}
            testId="library-empty-filtered"
          />
        ) : (
          <div className="sp-tile-grid" data-testid="library-grid">
            {filtered.map(c => {
              const bucket = availabilityBucket(c);
              const pill = availabilityPill(bucket);
              return (
                <article
                  key={c.id}
                  className="sp-tile is-clickable"
                  onClick={() => setDetail(c)}
                  data-testid={`library-tile-${c.id}`}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter') setDetail(c); }}
                >
                  <ComponentSilhouette brand={c.brand_name} name={c.name} />
                  <div className="sp-tile-body">
                    <div className="sp-tile-brand">{c.brand_name || 'Unknown brand'}</div>
                    <h4 className="sp-tile-name" title={c.name}>{c.name}</h4>
                    {c.size && <div className="sp-tile-size">{c.size}</div>}
                    {c.description && <p className="sp-tile-desc">{c.description}</p>}
                    <div className="sp-tile-footer">
                      <div className="sp-tile-meta">
                        <span><StatusPill variant={pill.variant}>{pill.label}</StatusPill></span>
                        <span className="sp-subtle">Available <strong className="sp-mono-num" style={{ color: 'var(--diis-ink)' }}>{c.stock_quantity}</strong></span>
                      </div>
                      <button
                        type="button"
                        className="sp-btn sp-btn-sm sp-btn-primary"
                        onClick={(e) => { e.stopPropagation(); openNewRequest({ component_id: c.id }); }}
                        data-testid={`library-request-btn-${c.id}`}
                      >
                        {bucket === 'out' ? 'Join waitlist' : 'Request'}
                      </button>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>

      {detail && (
        <ComponentDetailDrawer
          component={detail}
          onClose={() => setDetail(null)}
          onRequest={(qty) => { openNewRequest({ component_id: detail.id, quantity: qty }); setDetail(null); }}
        />
      )}

      {sheetOpen && (
        <MobileFilterSheet
          brands={brands}
          sizes={sizes}
          brandFilter={brandFilter}
          sizeFilter={sizeFilter}
          availabilityFilter={availabilityFilter}
          onChange={{ setBrandFilter, setSizeFilter, setAvailabilityFilter }}
          onClose={() => setSheetOpen(false)}
          onClear={() => { clearFilters(); setSheetOpen(false); }}
          resultCount={filtered.length}
        />
      )}
    </>
  );
}

/* ────────────────────────────────────────────────────────── *
 * Mobile filter bottom-sheet.
 * Renders only on the mobile breakpoint (parent .sp-filter-bar
 * is the only way to open it). Applies changes live — Apply just
 * dismisses the sheet.
 * ────────────────────────────────────────────────────────── */
function MobileFilterSheet({
  brands, sizes,
  brandFilter, sizeFilter, availabilityFilter,
  onChange, onClose, onClear, resultCount,
}) {
  const { setBrandFilter, setSizeFilter, setAvailabilityFilter } = onChange;
  return (
    <div className="sp-sheet-scrim" onClick={onClose} role="dialog" aria-modal="true">
      <div
        className="sp-sheet"
        onClick={(e) => e.stopPropagation()}
        data-testid="library-mobile-filter-sheet"
      >
        <div className="sp-sheet-handle" aria-hidden="true" />
        <header className="sp-sheet-header">
          <h3 className="sp-card-title">Filter Components</h3>
          <button
            type="button"
            className="sp-topbar-menu-btn"
            onClick={onClose}
            aria-label="Close filters"
            data-testid="library-mobile-filter-close"
          ><X size={16} /></button>
        </header>
        <div className="sp-sheet-body">
          <div>
            <label className="sp-field-label">Brand</label>
            <select
              className="sp-select"
              value={brandFilter}
              onChange={(e) => setBrandFilter(e.target.value)}
              data-testid="library-mobile-brand-filter"
            >
              <option value="all">All brands</option>
              {brands.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </div>
          <div>
            <label className="sp-field-label">Size / Specification</label>
            <select
              className="sp-select"
              value={sizeFilter}
              onChange={(e) => setSizeFilter(e.target.value)}
              disabled={sizes.length === 0}
              data-testid="library-mobile-size-filter"
            >
              <option value="all">All sizes</option>
              {sizes.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="sp-field-label">Availability</label>
            <select
              className="sp-select"
              value={availabilityFilter}
              onChange={(e) => setAvailabilityFilter(e.target.value)}
              data-testid="library-mobile-availability-filter"
            >
              <option value="all">All items</option>
              <option value="in-stock">In stock</option>
              <option value="low">Low stock</option>
              <option value="out">Out of stock</option>
            </select>
          </div>
        </div>
        <footer className="sp-sheet-footer">
          <button
            type="button"
            className="sp-btn sp-btn-ghost"
            onClick={onClear}
            data-testid="library-mobile-filter-clear"
          >Clear</button>
          <button
            type="button"
            className="sp-btn sp-btn-primary"
            onClick={onClose}
            data-testid="library-mobile-filter-apply"
          >Show {resultCount} component{resultCount === 1 ? '' : 's'}</button>
        </footer>
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────── *
 * Component detail drawer.
 * Shows every REAL field on the component plus the segregated
 * stock breakdown. Includes an inline qty stepper + Request CTA.
 * ────────────────────────────────────────────────────────── */
function ComponentDetailDrawer({ component: c, onClose, onRequest }) {
  const bucket = availabilityBucket(c);
  const pill = availabilityPill(bucket);
  const available = Number(c.stock_quantity || 0);
  const [qty, setQty] = useState(Math.max(1, Math.min(1, available || 1)));

  const total    = Number(c.total_stock || 0);
  const issued   = Number(c.issued_qty || 0);
  const reissue  = Number(c.reissuable_stock || 0);
  const returned = Number(c.returned_old_stock || 0);
  const denom = Math.max(1, total + reissue);
  const pctAvail   = ((available > 0 ? available : 0) / denom) * 100;
  const pctReissue = (reissue / denom) * 100;
  const pctIssued  = (issued / denom) * 100;

  const bump = (delta) => {
    const next = qty + delta;
    if (next < 1) return;
    setQty(next);
  };

  return (
    <div className="sp-drawer-scrim" onClick={onClose} role="dialog" aria-modal="true">
      <aside
        className="sp-drawer"
        onClick={(e) => e.stopPropagation()}
        data-testid="library-detail-drawer"
      >
        <header className="sp-drawer-header">
          <div style={{ minWidth: 0 }}>
            <div className="sp-tile-brand" style={{ color: 'var(--diis-accent-ink)' }}>{c.brand_name || 'Unknown brand'}</div>
            <h3 className="sp-card-title" style={{ marginTop: 4 }}>{c.name}</h3>
            {c.size && <p className="sp-card-subtitle">{c.size}</p>}
          </div>
          <button
            type="button"
            className="sp-topbar-menu-btn"
            onClick={onClose}
            aria-label="Close"
            data-testid="library-detail-close"
          ><X size={16} /></button>
        </header>

        <div className="sp-drawer-body">
          {/* Hero visual — hairline silhouette placeholder */}
          <div className="sp-hero" data-testid="library-detail-hero">
            <span className="sp-hero-badge">{c.brand_name || 'Component'}</span>
            <ComponentSilhouette brand={c.brand_name} name={c.name} size={96} badge={false} />
          </div>

          {/* Real fields only */}
          <div className="sp-mt-5">
            <dl className="sp-def-list">
              <div>
                <dt>Brand</dt>
                <dd>{c.brand_name || '—'}</dd>
              </div>
              <div>
                <dt>Size / Specification</dt>
                <dd style={{ fontFamily: 'var(--diis-font-mono)', letterSpacing: '0.02em' }}>{c.size || '—'}</dd>
              </div>
              {c.price !== null && c.price !== undefined && Number(c.price) > 0 && (
                <div>
                  <dt>Reference price</dt>
                  <dd className="sp-mono-num">₹{Number(c.price).toLocaleString('en-IN')}</dd>
                </div>
              )}
              <div>
                <dt>Type</dt>
                <dd>
                  {c.is_returnable
                    ? <StatusPill variant="info">Returnable</StatusPill>
                    : <StatusPill variant="neutral">Consumable · single-use</StatusPill>}
                </dd>
              </div>
            </dl>

            {c.description && (
              <div className="sp-mt-5">
                <div className="sp-eyebrow">Description</div>
                <p className="sp-mt-2" style={{ margin: 0, fontSize: 14, lineHeight: 1.6, color: 'var(--diis-ink-muted)' }}>
                  {c.description}
                </p>
              </div>
            )}
          </div>

          {/* Stock breakdown */}
          <div className="sp-mt-6">
            <div className="sp-flex-between">
              <span className="sp-eyebrow">Availability</span>
              <StatusPill variant={pill.variant}>{pill.label}</StatusPill>
            </div>

            <div className="sp-mt-3 sp-stock-bar" data-testid="library-stock-bar">
              <span className="fill-avail"   style={{ width: `${pctAvail}%` }}   title={`Available ${available}`} />
              <span className="fill-reissue" style={{ width: `${pctReissue}%` }} title={`Re-issuable ${reissue}`} />
              <span className="fill-issued"  style={{ width: `${pctIssued}%` }}  title={`Issued ${issued}`} />
            </div>
            <div className="sp-stock-legend">
              <div><em className="dot-avail" /> Available <strong style={{ color: 'var(--diis-ink)' }} className="sp-mono-num">{available}</strong></div>
              {reissue > 0 && <div><em className="dot-reissue" /> Re-issuable <strong style={{ color: 'var(--diis-ink)' }} className="sp-mono-num">{reissue}</strong></div>}
              <div><em className="dot-issued" /> In students' hands <strong style={{ color: 'var(--diis-ink)' }} className="sp-mono-num">{issued}</strong></div>
            </div>

            <div className="sp-mt-4" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
              <div className="sp-card" style={{ padding: 14 }}>
                <div className="sp-stat-label">Total (fresh)</div>
                <div className="sp-stat-value sp-mono-num" style={{ fontSize: 22, marginTop: 2 }}>{total}</div>
              </div>
              <div className="sp-card" style={{ padding: 14 }}>
                <div className="sp-stat-label">Older-stock returned</div>
                <div className="sp-stat-value sp-mono-num" style={{ fontSize: 22, marginTop: 2 }}>{returned}</div>
                <div className="sp-subtle" style={{ marginTop: 2 }}>Awaiting inspection</div>
              </div>
              <div className="sp-card" style={{ padding: 14 }}>
                <div className="sp-stat-label">Low-stock threshold</div>
                <div className="sp-stat-value sp-mono-num" style={{ fontSize: 22, marginTop: 2 }}>{c.low_stock_threshold || 0}</div>
              </div>
            </div>
          </div>
        </div>

        <footer className="sp-drawer-footer">
          <div className="sp-qty" data-testid="library-detail-qty">
            <button type="button" onClick={() => bump(-1)} disabled={qty <= 1} aria-label="Decrease"><Minus size={14} /></button>
            <input
              type="number"
              value={qty}
              min={1}
              onChange={(e) => setQty(Math.max(1, Number(e.target.value) || 1))}
              data-testid="library-detail-qty-input"
            />
            <button type="button" onClick={() => bump(1)} aria-label="Increase"><Plus size={14} /></button>
          </div>
          <button
            type="button"
            className="sp-btn sp-btn-accent"
            onClick={() => onRequest(qty)}
            data-testid="library-detail-request-btn"
          >
            {bucket === 'out' ? 'Join waitlist' : 'Request Component'}
          </button>
        </footer>
      </aside>
    </div>
  );
}
